import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Space, 
  Input, 
  InputNumber,
  Select, 
  DatePicker, 
  Tag, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Dropdown, 
  Modal, 
  message,
  Checkbox,
  Spin,
  Divider,
  Segmented,
  Result
} from 'antd';
import type { MenuProps } from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  FileTextOutlined,
  CarOutlined,
  RocketOutlined,
  SendOutlined,
  MoreOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { DataTable, ExecutionCell, PageLayout, StatusCell, getStatusLabel } from '../../ui';
import {
  RouteApplicationsCompactText,
  RouteClientsCompactText
} from '../../features/routes/RouteMultiAppCompactCells';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { 
  useDeleteRouteMutation, 
  useUpdateRouteStatusMutation,
  useCreateRoutePenaltyMutation,
  useFinalizeOperationalCompletionMutation,
  useGetApplicationsForFilterQuery
} from '../../store/api/routesApi';
import {
  useLazyGetCarrierAgreementByRouteQuery,
  useLazyGetCarrierAgreementPenaltiesQuery,
  type CarrierAgreementPenaltyRow
} from '../../store/api/carrierAgreementsApi';
import { useGetRoutesExtendedQuery } from '../../store/api/financeApi';
import { useGetUsersQuery } from '../../store/api/usersApi';
import { useGetCarriersQuery, useGetClientsQuery, useGetDriversQuery, useGetVehiclesQuery } from '../../store/api/referencesApi';
import { useCreateLocationMutation } from '../../store/api/locationsApi';
import { useCreateTNMutation } from '../../store/api/tnApi';
import { Route, RouteStatus, TransportType, RouteFilters } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { normalizeRouteStatus, getAllowedStatusTransitions as getAllowedStatusTransitionsUtil } from '../../utils/statusUtils';
import { isLiveRouteStatus } from '../../features/mutationWorkspace';
import {
  filterResponsibleEligibleUsers,
  formatResponsibleUserLabel,
} from '../../utils/userDisplayName';
import { formatApplicationSelectOptionLabel } from '../../utils/applicationDisplayFormat';
import { PENALTY_TYPE_LABELS } from '../../utils/penaltyTypeLabels';
import { buildRouteDirection } from '../../utils/addressUtils';
import { navigateToReferenceCard } from '../../utils/referenceCardNavigation';
import RouteCreateForm from '../../components/routes/RouteCreateForm';
import LocationModal from '../../components/common/LocationModal';
import LocationHistoryModal from '../../components/common/LocationHistoryModal';
import {
  mergeRoutesPageFilters,
  loadRoutesPageFiltersFromSession,
  saveRoutesPageFiltersToSession,
  type RoutesPageExecutionFilter,
  type RoutesPagePaymentFilter,
  type RoutesPagePreset,
  type RouteListScope,
} from './routesPageFilterPersistence';
import { ROUTES_PAGE_PRESETS, ROUTES_PAGE_PAYMENT_FILTER_OPTIONS } from './routesPagePresets';
import './RoutesPage.css';

function routeListDirectionLabel(record: {
  stops?: Array<{ city?: string | null; address?: string | null; position?: number | null }>;
  loadingCity?: string | null;
  unloadingCity?: string | null;
  loadingAddress?: string;
  unloadingAddress?: string;
}) {
  const fromStops = buildRouteDirection(record);
  if (fromStops !== 'Не указано') return fromStops;
  const from = record.loadingCity?.trim();
  const to = record.unloadingCity?.trim();
  if (from && to) return `${from} → ${to}`;
  return '—';
}

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const ROUTES_TABLE_SCROLL = { x: 'max-content' as const, y: 560 };

/** Состояние строк ручных штрафов при завершении маршрута (ключ — agreementPenaltyId). */
type CompletionPenaltyState = Record<
  number,
  {
    violated: boolean;
    days?: number | null;
    amount?: number | null;
    comment: string;
  }
>;


// getAllowedStatusTransitions helper removed (not used, use util instead)

// Переводы типов транспорта
const transportLabels = {
  [TransportType.AUTO]: 'Авто',
  [TransportType.RAILWAY]: 'ЖД',
  [TransportType.AVIATION]: 'Авиа'
};

// Иконки типов транспорта
const transportIcons = {
  [TransportType.AUTO]: <CarOutlined />,
  [TransportType.RAILWAY]: <RocketOutlined />,
  [TransportType.AVIATION]: <SendOutlined />
};

// Цвета иконок для быстpого визуального различия типов маршрутов
const transportIconColors: Record<TransportType, string> = {
  [TransportType.AUTO]: '#1890ff', // синий
  [TransportType.RAILWAY]: '#52c41a', // зелёный
  [TransportType.AVIATION]: '#fa8c16' // оранжевый
};

// Note: row background highlighting removed; using colored dot indicator instead

const ROUTE_STATUS_FILTER_OPTIONS = Object.values(RouteStatus).map((st) => ({
  value: st,
  label: getStatusLabel(st)
}));

export type { RoutesPageExecutionFilter, RoutesPagePersistedFilters } from './routesPageFilterPersistence';

const RoutesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isReadOnlyRole } = useAuth();
  const readOnly = isReadOnlyRole();
  const initialPersisted = useMemo(() => mergeRoutesPageFilters(loadRoutesPageFiltersFromSession()), []);

  const [filters, setFilters] = useState<
    RouteFilters & { page?: number; limit?: number; search?: string; executionScope?: RoutesPageExecutionFilter }
  >(() => ({
    page: initialPersisted.page ?? 1,
    limit: initialPersisted.limit ?? 20,
    executionScope: initialPersisted.executionScope ?? 'all',
    search: initialPersisted.search,
    transportType: initialPersisted.transportType as TransportType | undefined,
    status: initialPersisted.status,
    applicationId: initialPersisted.applicationId,
    dateFrom: initialPersisted.dateFrom,
    dateTo: initialPersisted.dateTo,
    responsibleLogistId: initialPersisted.responsibleLogistId,
    carrierId: initialPersisted.carrierId,
    clientId: initialPersisted.clientId,
    driverId: initialPersisted.driverId,
    vehicleId: initialPersisted.vehicleId,
  }));
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [newStatus, setNewStatus] = useState<RouteStatus | null>(null);
  const [actualDate, setActualDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [completionManualPenalties, setCompletionManualPenalties] = useState<CarrierAgreementPenaltyRow[]>([]);
  const [completionPenaltiesLoading, setCompletionPenaltiesLoading] = useState(false);
  const [completionPenaltiesLoadError, setCompletionPenaltiesLoadError] = useState<string | null>(null);
  const [penaltiesState, setPenaltiesState] = useState<CompletionPenaltyState>({});
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [selectedRouteForLocation, setSelectedRouteForLocation] = useState<Route | null>(null);
  const [isLocationHistoryModalVisible, setIsLocationHistoryModalVisible] = useState(false);
  const [selectedRouteForHistory, setSelectedRouteForHistory] = useState<Route | null>(null);
  const [activePreset, setActivePreset] = useState<RoutesPagePreset>(initialPersisted.preset);
  const [paymentFilter, setPaymentFilter] = useState<RoutesPagePaymentFilter>(initialPersisted.paymentFilter);
  const [listScope, setListScope] = useState<RouteListScope>(initialPersisted.listScope ?? 'active');
  // API queries and mutations (obligation-based routes-extended)
  const { data: routesData, isLoading, refetch, isError, error } = useGetRoutesExtendedQuery({
    page: filters.page,
    limit: filters.limit,
    view: activePreset === 'all' ? undefined : activePreset,
    includePresetCounts: true,
    paymentStatus: paymentFilter === 'all' ? undefined : paymentFilter,
    transportType: filters.transportType,
    status: filters.status,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    applicationId: filters.applicationId,
    executionScope: filters.executionScope && filters.executionScope !== 'all' ? filters.executionScope : undefined,
    responsibleLogistId: (filters as any).responsibleLogistId,
    carrierId: (filters as any).carrierId,
    clientId: (filters as any).clientId,
    driverId: (filters as any).driverId,
    vehicleId: (filters as any).vehicleId,
    search: filters.search,
    scope: listScope,
  });
  const { data: applicationsData } = useGetApplicationsForFilterQuery({ limit: 100 });
  const { data: usersData } = useGetUsersQuery();
  const { data: carriersData } = useGetCarriersQuery();
  const { data: clientsData } = useGetClientsQuery();
  const { data: driversData } = useGetDriversQuery();
  const { data: vehiclesData } = useGetVehiclesQuery();
  const [deleteRoute] = useDeleteRouteMutation();
  const [updateRouteStatus] = useUpdateRouteStatusMutation();
  const [finalizeOperationalCompletion] = useFinalizeOperationalCompletionMutation();
  const [createRoutePenalty] = useCreateRoutePenaltyMutation();
  const [fetchAgreementByRoute] = useLazyGetCarrierAgreementByRouteQuery();
  const [fetchAgreementPenalties] = useLazyGetCarrierAgreementPenaltiesQuery();
  const [createLocation] = useCreateLocationMutation();
  const [createTN] = useCreateTNMutation();

  // Обработчики фильтров
  // Filter handlers defined inline in JSX; helpers removed to reduce lint noise

  // Обработчики действий
  const openRoutePage = React.useCallback(
    (route: Route) => {
      navigate(`/routes/${route.id}`);
    },
    [navigate]
  );

  const routeTableOnRow = useCallback(
    (record: Route) => ({
      onClick: (e: React.MouseEvent) => {
        const t = e.target as HTMLElement;
        if (
          t.closest(
            'a, button, .ant-btn, [role="button"], .ant-dropdown, .ant-select, input, textarea, .ant-picker, [data-routes-table-interactive]'
          )
        ) {
          return;
        }
        openRoutePage(record);
      },
      style: { cursor: 'pointer' } as const
    }),
    [openRoutePage]
  );

  const handleEdit = useCallback((route: Route) => {
    setSelectedRoute(route);
    setIsEditModalVisible(true);
  }, []);

  const performHardDelete = useCallback(
    async (route: Route) => {
      try {
        await deleteRoute(route.id).unwrap();
        message.success('Маршрут удалён');
        refetch();
      } catch (error: any) {
        const errorMessage = error?.data?.error || error?.message || 'Ошибка при удалении маршрута';
        message.error(errorMessage);
      }
    },
    [deleteRoute, refetch]
  );

  const performCancelRoute = useCallback(
    async (route: Route) => {
      try {
        await updateRouteStatus({ id: route.id, status: RouteStatus.CANCELLED, actualDate: new Date().toISOString() }).unwrap();
        message.success('Маршрут переведён в статус «Отменён»');
        refetch();
      } catch (error: any) {
        const errorMessage = error?.data?.error || error?.data?.message || error?.message || 'Ошибка отмены маршрута';
        message.error(errorMessage);
      }
    },
    [refetch, updateRouteStatus]
  );

  const handleDeleteOrCancel = useCallback(
    (route: Route) => {
      const st = normalizeRouteStatus(route.status) ?? RouteStatus.CREATED;
      const isCreated = st === RouteStatus.CREATED;
      if (isCreated) {
        Modal.confirm({
          title: 'Удаление маршрута',
          content: `Удалить маршрут №${route.id}? Это действие нельзя отменить.`,
          okText: 'Удалить',
          cancelText: 'Отмена',
          okButtonProps: { danger: true },
          onOk: () => performHardDelete(route)
        });
        return;
      }
      if (st === RouteStatus.CANCELLED || st === RouteStatus.COMPLETED) {
        return;
      }
      Modal.confirm({
        title: 'Отмена маршрута',
        content: `Маршрут №${route.id} будет переведён в статус «Отменён». Продолжить?`,
        okText: 'Отменить маршрут',
        okButtonProps: { danger: true },
        cancelText: 'Закрыть',
        onOk: () => performCancelRoute(route)
      });
    },
    [performCancelRoute, performHardDelete]
  );

  const handleChangeStatus = useCallback((route: Route) => {
    setSelectedRoute(route);
    setNewStatus(null);
    setActualDate(route.actualUnloadingDate || '');
    setNotes('');
    setCompletionManualPenalties([]);
    setPenaltiesState({});
    setCompletionPenaltiesLoadError(null);
    setIsStatusModalVisible(true);
  }, []);

  const updateCompletionPenaltyRow = React.useCallback(
    (agreementPenaltyId: number, patch: Partial<CompletionPenaltyState[number]>) => {
      setPenaltiesState((prev) => {
        const cur = prev[agreementPenaltyId];
        if (!cur) return prev;
        return { ...prev, [agreementPenaltyId]: { ...cur, ...patch } };
      });
    },
    []
  );

  useEffect(() => {
    if (!isStatusModalVisible || !selectedRoute) return;

    if (newStatus !== RouteStatus.COMPLETED) {
      setCompletionManualPenalties([]);
      setPenaltiesState({});
      setCompletionPenaltiesLoadError(null);
      setCompletionPenaltiesLoading(false);
      return;
    }

    if (selectedRoute.isCompanyRoute) {
      setCompletionManualPenalties([]);
      setPenaltiesState({});
      setCompletionPenaltiesLoadError(null);
      setCompletionPenaltiesLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setCompletionPenaltiesLoading(true);
      setCompletionPenaltiesLoadError(null);
      try {
        const agreement = await fetchAgreementByRoute(selectedRoute.id).unwrap();
        const list = await fetchAgreementPenalties(agreement.id).unwrap();
        if (cancelled) return;
        const manual = list.filter((p) => p.requiresManualInput);
        setCompletionManualPenalties(manual);
        const initial: CompletionPenaltyState = {};
        for (const p of manual) {
          initial[p.id] = {
            violated: false,
            days: p.perDay ? 1 : null,
            amount: p.type === 'FIXED' ? Number(p.value) : null,
            comment: ''
          };
        }
        setPenaltiesState(initial);
      } catch (e: any) {
        if (cancelled) return;
        setCompletionManualPenalties([]);
        setPenaltiesState({});
        const msg = e?.data?.message || e?.error || e?.message || 'Не удалось загрузить штрафы договора';
        setCompletionPenaltiesLoadError(String(msg));
      } finally {
        if (!cancelled) setCompletionPenaltiesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isStatusModalVisible,
    selectedRoute,
    newStatus,
    fetchAgreementByRoute,
    fetchAgreementPenalties
  ]);

  const lockedRouteStatusLabel = useMemo(() => {
    if (!selectedRoute) return '';
    return getStatusLabel(normalizeRouteStatus(selectedRoute.status) || String(RouteStatus.CREATED));
  }, [selectedRoute]);

  useEffect(() => {
    if (listScope === 'active' && activePreset === 'completed') {
      setListScope('archive');
    }
  }, [activePreset, listScope]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: 1 }));
  }, [activePreset, paymentFilter]);

  useEffect(() => {
    saveRoutesPageFiltersToSession({
      preset: activePreset,
      paymentFilter,
      search: filters.search,
      transportType: filters.transportType,
      status: filters.status,
      applicationId: filters.applicationId,
      executionScope: filters.executionScope ?? 'all',
      responsibleLogistId: (filters as any).responsibleLogistId,
      carrierId: (filters as any).carrierId,
      clientId: (filters as any).clientId,
      driverId: (filters as any).driverId,
      vehicleId: (filters as any).vehicleId,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      listScope,
      page: filters.page,
      limit: filters.limit,
    });
  }, [activePreset, paymentFilter, filters, listScope]);

  const handlePresetChange = useCallback((preset: RoutesPagePreset) => {
    setActivePreset(preset);
    const def = ROUTES_PAGE_PRESETS.find((p) => p.key === preset);
    if (def?.requiresShowCompleted) {
      setListScope('archive');
    }
  }, []);

  const handleStatusUpdate = async () => {
    if (!selectedRoute || !newStatus) return;
    if (!actualDate) {
      message.error('Укажите дату изменения статуса');
      return;
    }

    try {
      if (newStatus === RouteStatus.COMPLETED) {
        if (!selectedRoute.isCompanyRoute && completionManualPenalties.length > 0) {
          for (const row of completionManualPenalties) {
            const st = penaltiesState[row.id];
            if (!st?.violated) continue;

            if (row.perDay) {
              const d = st.days ?? null;
              if (d === null || !Number.isFinite(Number(d)) || Number(d) < 1) {
                message.error(`Укажите число дней (≥ 1) для: ${row.name}`);
                return;
              }
            }

            if (row.requiresComment && !(st.comment || '').trim()) {
              message.error(`Укажите комментарий для: ${row.name}`);
              return;
            }

            if (String(row.type).toUpperCase() === 'FIXED') {
              const amt = st.amount;
              if (amt === null || amt === undefined || !Number.isFinite(Number(amt)) || Number(amt) < 0) {
                message.error(`Укажите сумму для: ${row.name}`);
                return;
              }
              if (Number(row.value) === 0 && Number(amt) <= 0) {
                message.error(`Укажите сумму больше 0 для: ${row.name}`);
                return;
              }
            }
          }

          for (const row of completionManualPenalties) {
            const st = penaltiesState[row.id];
            if (!st?.violated) continue;
            await createRoutePenalty({
              routeId: selectedRoute.id,
              agreementPenaltyId: row.id,
              days: row.perDay ? Number(st.days) : undefined,
              comment: (st.comment || '').trim() || undefined,
              amount:
                String(row.type).toUpperCase() === 'FIXED' && st.amount != null
                  ? Number(st.amount)
                  : undefined
            }).unwrap();
          }
        }

        const logistComment = (notes || '').trim();
        if (logistComment.length < 3) {
          message.error('Комментарий логиста обязателен (минимум 3 символа)');
          return;
        }

        await finalizeOperationalCompletion({
          routeId: selectedRoute.id,
          logistComment
        }).unwrap();
        message.success('Маршрут завершён');
        setIsStatusModalVisible(false);
        setCompletionManualPenalties([]);
        setPenaltiesState({});
        setCompletionPenaltiesLoadError(null);
        refetch();
        return;
      }

      console.debug('🛠️ Update route status payload:', {
        id: selectedRoute.id,
        status: newStatus,
        actualDate,
        notes
      });

      await updateRouteStatus({
        id: selectedRoute.id,
        status: newStatus,
        actualDate: actualDate || undefined,
        notes: notes || undefined
      }).unwrap();
      message.success('Статус маршрута успешно обновлен');
      setIsStatusModalVisible(false);
      setCompletionManualPenalties([]);
      setPenaltiesState({});
      setCompletionPenaltiesLoadError(null);
      refetch();
    } catch (error: any) {
      console.error('❌ Update route status error:', error);
      const errorMessage = error?.data?.message || error?.data?.error || error?.message || 'Ошибка при обновлении статуса';
      message.error(errorMessage);
    }
  };

  // Применить applicationId из URL при открытии страницы
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appId = params.get('applicationId');
    if (appId) {
      setFilters(prev => ({ ...prev, applicationId: Number(appId) }));
    }
  }, []);

  const handleSaveLocation = async (locationData: {
    latitude: number;
    longitude: number;
    address?: string;
    recordedAt: Date;
  }, cargoId?: number) => {
    if (!selectedRouteForLocation) return;

    try {
      console.debug('🚀 RoutesPage: Начинаем сохранение местоположения...');
      console.debug('📋 RoutesPage: Данные для сохранения:', locationData);
      console.debug('📋 RoutesPage: Выбранный маршрут:', selectedRouteForLocation);
      console.debug('📦 RoutesPage: Грузы в маршруте:', selectedRouteForLocation.cargo);
      console.debug('📦 RoutesPage: Первый груз:', selectedRouteForLocation.cargo?.[0]);

      // Используем переданный cargoId или получаем первый груз из маршрута
      const targetCargoId = cargoId || selectedRouteForLocation.cargo?.[0]?.id;
      console.debug('📦 RoutesPage: Переданный cargoId:', cargoId);
      console.debug('📦 RoutesPage: Полученный из маршрута cargoId:', selectedRouteForLocation.cargo?.[0]?.id);
      console.debug('📦 RoutesPage: Итоговый targetCargoId:', targetCargoId);
      
      if (!targetCargoId) {
        console.error('❌ RoutesPage: Нет cargoId!');
        console.error('❌ RoutesPage: cargoId из параметра:', cargoId);
        console.error('❌ RoutesPage: cargo из маршрута:', selectedRouteForLocation.cargo);
        message.error('В маршруте нет грузов');
        return;
      }

      console.debug('📦 RoutesPage: Используемый cargoId:', targetCargoId);

      const requestData = {
        routeId: selectedRouteForLocation.id,
        cargoId: targetCargoId,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        address: locationData.address,
        recordedAt: locationData.recordedAt.toISOString()
      };

      console.debug('📤 RoutesPage: Отправляем запрос:', requestData);

      await createLocation(requestData).unwrap();

      console.debug('✅ RoutesPage: Местоположение успешно сохранено');
      message.success('Местоположение успешно сохранено');
      setIsLocationModalVisible(false);
      
      // Добавляем задержку перед обновлением данных
      setTimeout(() => {
        console.debug('🔄 RoutesPage: Обновляем данные...');
        refetch();
      }, 100);
    } catch (error: any) {
      console.error('❌ RoutesPage: Save location error:', error);
      console.error('❌ RoutesPage: Error details:', {
        message: error?.message,
        data: error?.data,
        status: error?.status,
        stack: error?.stack
      });
      const errorMessage = error?.data?.error || error?.message || 'Ошибка при сохранении местоположения';
      message.error(errorMessage);
    }
  };

  const openEntityCard = useCallback(
    (path: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      navigateToReferenceCard(navigate, path, location);
    },
    [navigate, location]
  );

  // Колонки таблицы — упрощённый операционный монитор (S1.1)
  const columns: ColumnsType<Route> = useMemo(() => [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 56,
      className: 'primary-cell',
      render: (id: number) => <span>#{id}</span>,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Заявка / Договор',
      key: 'applicationOrContract',
      render: (_, record) => {
        const contractNum = record.carrierContractRequestNumber;
        const contractDate = record.carrierContractRequestDate;
        if (record.applications?.length) {
          return (
            <RouteApplicationsCompactText
              applications={record.applications as any}
              singleAppUseApplicationCell
            />
          );
        }
        if (contractNum) {
          return (
            <div style={{ fontSize: '12px' }}>
              <div>{contractNum}</div>
              {contractDate && <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>{dayjs(contractDate).format('DD.MM.YY')}</div>}
            </div>
          );
        }
        return <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>;
      },
    },
    {
      title: 'Тип',
      dataIndex: 'transportType',
      key: 'transportType',
      width: 48,
      render: (type: TransportType) => (
        <span style={{ color: transportIconColors[type], fontSize: 18, display: 'inline-flex', alignItems: 'center' }} title={transportLabels[type]}>
          {transportIcons[type]}
        </span>
      ),
    },
    {
      title: 'Маршрут',
      key: 'route',
      render: (_, record) => {
        const routeText = routeListDirectionLabel(record as any);
        return (
          <span style={{ fontSize: '12px', color: 'var(--text)' }} title={routeText}>
            {routeText.length > 50 ? routeText.substring(0, 50) + '…' : routeText}
          </span>
        );
      },
      sorter: (a, b) => routeListDirectionLabel(a as any).localeCompare(routeListDirectionLabel(b as any)),
    },
    {
      title: 'Клиент',
      key: 'client',
      render: (_, record) => (
        <RouteClientsCompactText
          applications={record.applications as any}
          fallbackClient={record.client ?? null}
          truncate={18}
          style={{ fontSize: 12, color: 'var(--text)' }}
          renderPrimaryLink={({ clientId, label }) => (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/clients/${clientId}`)}
              style={{ fontSize: 12, height: 'auto', padding: '0 2px' }}
            >
              {label}
            </Button>
          )}
        />
      ),
    },
    {
      title: 'Исполнение',
      key: 'execution',
      width: 150,
      render: (_, record) => (
        <ExecutionCell
          route={{
            isCompanyRoute: record.isCompanyRoute,
            carrierContractRequestNumber: record.carrierContractRequestNumber,
            carrierContractRequestDate: record.carrierContractRequestDate,
            carrierAgreement: record.carrierAgreement
          }}
        />
      )
    },
    {
      title: 'Перевозчик',
      key: 'carrier',
      render: (_, record) => {
        const name = record.carrier?.name || '—';
        const cid = record.carrierId ?? record.carrier?.id;
        if (cid != null && name !== '—') {
          return (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/carriers/${cid}`)}
              style={{ fontSize: 12, height: 'auto', padding: '0 2px' }}
            >
              {name.length > 16 ? name.substring(0, 16) + '…' : name}
            </Button>
          );
        }
        return <span style={{ fontSize: '12px', color: 'var(--text)' }}>{name === '—' ? name : name}</span>;
      },
    },
    {
      title: 'Водитель / ТС',
      key: 'driverVehicle',
      render: (_, record) => {
        const did = record.driverId ?? record.driver?.id;
        const driverName = record.driver?.fullName;
        const truck = [record.vehicle?.brand, record.vehicle?.registrationNumber].filter(Boolean).join(' ');
        const truckId = record.vehicleId ?? record.vehicle?.id;
        const trailer = record.trailerVehicle
          ? [record.trailerVehicle.brand, record.trailerVehicle.registrationNumber].filter(Boolean).join(' ')
          : '';
        const trailerId = record.trailerVehicleId ?? record.trailerVehicle?.id;
        return (
          <div style={{ fontSize: '12px', lineHeight: 1.3 }}>
            {driverName ? (
              did != null ? (
                <div>
                  <Button
                    type="link"
                    className="routes-entity-link"
                    size="small"
                    onClick={openEntityCard(`/drivers/${did}`)}
                    style={{ fontSize: 12, height: 'auto', padding: 0 }}
                  >
                    {driverName}
                  </Button>
                </div>
              ) : (
                <div>{driverName}</div>
              )
            ) : null}
            {truck || trailer ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                {truckId != null && truck ? (
                  <Button
                    type="link"
                    className="routes-entity-link"
                    size="small"
                    onClick={openEntityCard(`/vehicles/${truckId}`)}
                    style={{ fontSize: 11, height: 'auto', padding: 0 }}
                  >
                    {truck}
                  </Button>
                ) : (
                  <span>{truck}</span>
                )}
                {trailer ? (
                  trailerId != null ? (
                    <>
                      {' + '}
                      <Button
                        type="link"
                        className="routes-entity-link"
                        size="small"
                        onClick={openEntityCard(`/vehicles/${trailerId}`)}
                        style={{ fontSize: 11, height: 'auto', padding: 0 }}
                      >
                        {trailer}
                      </Button>
                    </>
                  ) : (
                    <span> + {trailer}</span>
                  )
                ) : null}
              </div>
            ) : !driverName ? (
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Даты',
      key: 'dates',
      render: (_, record) => {
        const fmt = (d: string | undefined) => (d ? dayjs(d).format('DD.MM') : '—');
        const pLoad = fmt(record.plannedLoadingDate);
        const pUnload = fmt(record.plannedUnloadingDate);
        const aLoad = fmt(record.actualLoadingDate);
        const aUnload = fmt(record.actualUnloadingDate);
        return (
          <div style={{ fontSize: '11px', lineHeight: 1.4 }}>
            <div>{pLoad} → {pUnload}</div>
            <div style={{ color: 'var(--text-secondary)' }}>{aLoad} → {aUnload}</div>
          </div>
        );
      },
      sorter: (a, b) => {
        const aDate = a.plannedLoadingDate ? new Date(a.plannedLoadingDate).getTime() : 0;
        const bDate = b.plannedLoadingDate ? new Date(b.plannedLoadingDate).getTime() : 0;
        return aDate - bDate;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: RouteStatus | string) => (
        <StatusCell status={String(status || '')} style={{ fontSize: '11px', padding: '0 6px', margin: 0 }} />
      ),
      sorter: (a, b) => {
        const order: Record<string, number> = {
          CREATED: 1,
          IN_TRANSIT: 2,
          ISSUES: 3,
          COMPLETED: 4,
          CANCELLED: 5,
        };
        const aNorm = String(a.status || '').toUpperCase();
        const bNorm = String(b.status || '').toUpperCase();
        return (order[aNorm] || 0) - (order[bNorm] || 0);
      },
    },
    {
      title: 'Оплата',
      key: 'aggregatedStatus',
      render: (_: string, record: any) => (
        <StatusCell status={record.aggregatedStatus?.overallStatus ?? 'OPEN'} />
      ),
    },
    {
      title: '🚨',
      key: 'controlIssues',
      width: 44,
      render: (_: string, record: any) => {
        const ci = record.controlIssues;
        const body = (() => {
        if (!ci) return <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>;
        if (ci.hasCritical) return <Tag color="error" style={{ fontSize: '11px', padding: '0 4px', margin: 0 }}>{ci.totalCount}</Tag>;
        if (ci.warnings?.length > 0) return <Tag color="warning" style={{ fontSize: '11px', padding: '0 4px', margin: 0 }}>{ci.totalCount}</Tag>;
        if (ci.info?.length > 0) return <Tag color="processing" style={{ fontSize: '11px', padding: '0 4px', margin: 0 }}>{ci.totalCount}</Tag>;
        return <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>;
        })();
        return body;
      },
    },
    {
      title: 'Логист',
      key: 'responsibleLogist',
      render: (_, record) => {
        const name = formatResponsibleUserLabel(record.responsibleLogist);
        return <span style={{ fontSize: '12px', color: 'var(--text)' }} title={name}>{name.length > 14 ? name.substring(0, 14) + '…' : name}</span>;
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const st = normalizeRouteStatus(record.status) ?? RouteStatus.CREATED;
        const isTerminal = st === RouteStatus.CANCELLED || st === RouteStatus.COMPLETED;
        const canOpenRouteEditor =
          st === RouteStatus.CREATED ||
          st === RouteStatus.READY_FOR_COMPLETION ||
          st === RouteStatus.ISSUES ||
          isLiveRouteStatus(st);
        const deleteLabel = st === RouteStatus.CREATED ? 'Удалить' : 'Удалить (отменить)';
        const actionItems: MenuProps['items'] = [
          {
            key: 'create_tn',
            label: 'Создать ТН',
            icon: <FileTextOutlined />,
            onClick: async () => {
              try {
                const result: any = await createTN({ routeId: record.id }).unwrap();
                message.success(`ТН #${result.number} создана`);
              } catch (e: any) {
                message.error(e?.data?.error || 'Ошибка создания ТН');
              }
            }
          },
          {
            key: 'edit',
            label: 'Редактировать',
            icon: <EditOutlined />,
            disabled: !canOpenRouteEditor,
            onClick: () => handleEdit(record)
          },
          { type: 'divider', key: 'before-delete' },
          {
            key: 'delete',
            label: deleteLabel,
            icon: <DeleteOutlined />,
            danger: true,
            disabled: isTerminal,
            onClick: () => handleDeleteOrCancel(record)
          }
        ];

        if (readOnly) {
          return <Text type="secondary">Только просмотр</Text>;
        }

        return (
          <span data-routes-table-interactive onClick={(e) => e.stopPropagation()}>
            <Space size={4}>
              <Button size="small" icon={<SyncOutlined />} onClick={() => handleChangeStatus(record)}>
                Статус
              </Button>
              <Dropdown menu={{ items: actionItems }} trigger={['click']} placement="bottomRight">
                <Button size="small" icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          </span>
        );
      }
    }
  ], [createTN, handleChangeStatus, handleDeleteOrCancel, handleEdit, openEntityCard, readOnly]);

  const tabCounts = routesData?.tabCounts;
  const presetCount = useCallback(
    (key: (typeof ROUTES_PAGE_PRESETS)[number]['countKey']) => {
      if (!key || !tabCounts) return 0;
      return (tabCounts as any)[key] ?? 0;
    },
    [tabCounts]
  );
  const tableRoutes = routesData?.routes ?? [];
  const paginationTotal = routesData?.pagination?.total ?? 0;

  const statistics = routesData?.statistics ?? null;

  const routesTablePagination = {
    current: filters.page,
    pageSize: filters.limit,
    total: paginationTotal,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} из ${total} записей`,
    onChange: (page: number, limit: number) => setFilters((prev) => ({ ...prev, page, limit })),
  };

  const routesTable = (
    <DataTable
      columns={columns}
      dataSource={tableRoutes}
      loading={isLoading}
      rowKey="id"
      onRow={routeTableOnRow}
      rootClassName="routes-page-routes-table"
      rowClassName={(record: any) => {
        const ci = record.controlIssues;
        if (ci?.hasCritical) return 'row-overdue';
        if (ci?.warnings?.length > 0) return 'row-warning';
        return '';
      }}
      scroll={ROUTES_TABLE_SCROLL}
      size="small"
      pagination={routesTablePagination}
    />
  );

  if (isError) {
    const status =
      error && typeof error === 'object' && 'status' in error ? (error as { status?: number }).status : undefined;
    const isForbidden = status === 403;
    return (
      <PageLayout title="Маршруты" subtitle="Управление маршрутами всех типов транспорта">
        <Result
          status={isForbidden ? 403 : 'error'}
          title={isForbidden ? 'Нет доступа к финансовым данным маршрутов' : 'Не удалось загрузить маршруты'}
          subTitle={
            isForbidden
              ? 'Требуется право routes.finance_view или finance.view для просмотра финансовых показателей маршрутов.'
              : 'Проверьте соединение и попробуйте снова.'
          }
          extra={
            <Button type="primary" onClick={() => void refetch()}>
              Повторить
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Маршруты" subtitle="Управление маршрутами всех типов транспорта">
      {/* Статистика */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={4}>
            <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
              <Statistic title="Всего маршрутов" value={statistics.total} />
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
              <Statistic 
                title="Авто" 
                value={statistics.auto} 
                prefix={<CarOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
              <Statistic 
                title="ЖД" 
                value={statistics.railway} 
                prefix={<RocketOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
              <Statistic 
                title="Авиа" 
                value={statistics.aviation} 
                prefix={<SendOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
              <Statistic 
                title="В пути" 
                value={statistics.inTransit} 
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
              <Statistic 
                title="Доставлено" 
                value={statistics.delivered} 
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Фильтры */}
      <Card style={{ marginBottom: '24px', backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder="Поиск по адресам, контрагентам..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Тип транспорта"
              value={filters.transportType}
              onChange={(value) => setFilters(prev => ({ ...prev, transportType: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={TransportType.AUTO}>Авто</Option>
              <Option value={TransportType.RAILWAY}>ЖД</Option>
              <Option value={TransportType.AVIATION}>Авиа</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Статус"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              {ROUTE_STATUS_FILTER_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Оплата"
              value={paymentFilter}
              onChange={(value) => setPaymentFilter((value ?? 'all') as RoutesPagePaymentFilter)}
              style={{ width: '100%' }}
            >
              {ROUTES_PAGE_PAYMENT_FILTER_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Заявка"
              value={filters.applicationId}
              onChange={(value) => setFilters(prev => ({ ...prev, applicationId: value }))}
              allowClear
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {applicationsData?.applications?.map((app: any) => {
                const label = formatApplicationSelectOptionLabel(app);
                return (
                <Option key={app.id} value={app.id}>
                  {label}
                </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Исполнение"
              value={filters.executionScope ?? 'all'}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, executionScope: (value ?? 'all') as RoutesPageExecutionFilter }))
              }
              style={{ width: '100%' }}
            >
              <Option value="all">Все</Option>
              <Option value="own">Свой транспорт</Option>
              <Option value="carriers">Перевозчики</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Логист"
              value={(filters as any).responsibleLogistId}
              onChange={(value) => setFilters(prev => ({ ...prev, responsibleLogistId: value } as any))}
              allowClear
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {filterResponsibleEligibleUsers(usersData).map((u: any) => {
                const label = formatResponsibleUserLabel(u);
                return (
                  <Option key={u.id} value={u.id} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Перевозчик"
              value={(filters as any).carrierId}
              onChange={(value) => setFilters(prev => ({ ...prev, carrierId: value } as any))}
              allowClear
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {(carriersData || []).map((c: any) => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <RangePicker
              placeholder={['Дата от', 'Дата до']}
              onChange={(dates) => {
                if (dates) {
                  setFilters(prev => ({
                    ...prev,
                    dateFrom: dates[0]?.format('YYYY-MM-DD'),
                    dateTo: dates[1]?.format('YYYY-MM-DD')
                  }));
                } else {
                  setFilters(prev => ({
                    ...prev,
                    dateFrom: undefined,
                    dateTo: undefined
                  }));
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={2}>
            <Button
              onClick={() => {
                setActivePreset('all');
                setPaymentFilter('all');
                setListScope('active');
                setFilters({
                  page: 1,
                  limit: 20,
                  executionScope: 'all',
                  search: undefined,
                  transportType: undefined,
                  status: undefined,
                  applicationId: undefined,
                  dateFrom: undefined,
                  dateTo: undefined,
                  responsibleLogistId: undefined,
                  carrierId: undefined,
                  clientId: undefined,
                  driverId: undefined,
                  vehicleId: undefined,
                } as RouteFilters & { page?: number; limit?: number; search?: string; executionScope?: RoutesPageExecutionFilter; responsibleLogistId?: number; carrierId?: number; clientId?: number; driverId?: number; vehicleId?: number });
              }}
              block
            >
              Сбросить
            </Button>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Клиент"
              value={(filters as any).clientId}
              onChange={(value) => setFilters((prev) => ({ ...prev, clientId: value } as any))}
              allowClear
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {(clientsData || []).map((c: any) => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Водитель"
              value={(filters as any).driverId}
              onChange={(value) => setFilters((prev) => ({ ...prev, driverId: value } as any))}
              allowClear
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {(driversData || []).map((d: any) => (
                <Option key={d.id} value={d.id}>{d.fullName || d.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="ТС"
              value={(filters as any).vehicleId}
              onChange={(value) => setFilters((prev) => ({ ...prev, vehicleId: value } as any))}
              allowClear
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {(vehiclesData || []).map((v: any) => (
                <Option key={v.id} value={v.id}>
                  {[v.brand, v.registrationNumber].filter(Boolean).join(' ') || `#${v.id}`}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row gutter={16} align="middle" style={{ marginTop: 12 }}>
          <Col span={8}>
            <Segmented
              value={listScope}
              onChange={(value) => {
                setListScope(value as RouteListScope);
                setFilters((prev) => ({ ...prev, page: 1 }));
              }}
              options={[
                { label: 'Активные', value: 'active' },
                { label: 'Архив', value: 'archive' },
              ]}
              block
            />
          </Col>
          <Col span={16}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Период фильтрует по дате создания маршрута. Пресеты и фильтры оплаты/исполнения сохранены.
            </Text>
          </Col>
        </Row>
      </Card>

      {!readOnly && (
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedRoute(null);
              setIsCreateFormVisible(true);
            }}
            size="large"
          >
            Создать маршрут
          </Button>
        </div>
      )}

      {/* Таблица маршрутов с быстрыми пресетами */}
      <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <Space wrap size={[8, 8]} style={{ marginBottom: 16 }}>
          {ROUTES_PAGE_PRESETS.map((preset) => (
            <Button
              key={preset.key}
              type={activePreset === preset.key ? 'primary' : 'default'}
              onClick={() => handlePresetChange(preset.key)}
            >
              {preset.label}
              <Tag
                color={activePreset === preset.key ? 'geekblue' : 'default'}
                style={{ marginInlineStart: 8, fontSize: 10, padding: '0 4px' }}
              >
                {presetCount(preset.countKey)}
              </Tag>
            </Button>
          ))}
        </Space>
        {routesTable}
      </Card>

      {/* Модальное окно смены статуса */}
      <Modal
        title="Изменение статуса маршрута"
        open={isStatusModalVisible}
        onOk={handleStatusUpdate}
        onCancel={() => {
          setIsStatusModalVisible(false);
          setCompletionManualPenalties([]);
          setPenaltiesState({});
          setCompletionPenaltiesLoadError(null);
        }}
        okText="Сохранить"
        cancelText="Отмена"
        okButtonProps={{
          disabled:
            !newStatus ||
            (!!selectedRoute &&
              (selectedRoute.status === RouteStatus.COMPLETED || selectedRoute.status === RouteStatus.CANCELLED)) ||
            Boolean(
              newStatus === RouteStatus.COMPLETED &&
                selectedRoute &&
                !selectedRoute.isCompanyRoute &&
                completionPenaltiesLoading
            )
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {selectedRoute && (selectedRoute.status === RouteStatus.COMPLETED || selectedRoute.status === RouteStatus.CANCELLED) ? (
            <div style={{ 
              padding: '12px', 
              backgroundColor: 'var(--card)', 
              border: '1px solid var(--card-border)', 
              borderRadius: '6px',
              color: 'var(--text)'
            }}>
              <strong>Статус маршрута нельзя изменить</strong>
              <br />
              Маршрут со статусом «{lockedRouteStatusLabel}» не может быть изменен.
            </div>
          ) : (
            <>
              <div>
                <label>Новый статус:</label>
                <Select
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="Выберите статус"
                  value={newStatus}
                  onChange={setNewStatus}
                >
                  {getAllowedStatusTransitionsUtil(normalizeRouteStatus(selectedRoute?.status) || RouteStatus.CREATED).map(
                    (status) => {
                      const transitionLabel = getStatusLabel(String(status));
                      return (
                    <Option key={status} value={status}>
                      {transitionLabel}
                    </Option>
                      );
                    }
                  )}
                </Select>
              </div>
              
              {newStatus && (
                <div>
                  <label>Дата изменения статуса:</label>
                  <DatePicker
                    showTime
                    style={{ width: '100%', marginTop: '8px' }}
                    placeholder="Выберите дату и время"
                    value={actualDate ? dayjs(actualDate) : undefined}
                    onChange={(date) => setActualDate(date ? date.toISOString() : '')}
                  />
                </div>
              )}
              
              <div>
                <label>Комментарий (опционально):</label>
                <Input.TextArea
                  style={{ marginTop: '8px' }}
                  placeholder="Дополнительная информация..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {newStatus === RouteStatus.COMPLETED && selectedRoute && !selectedRoute.isCompanyRoute && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <div>
                    <Space align="center">
                      <Text strong>Фиксация нарушений</Text>
                      {completionPenaltiesLoading ? <Spin size="small" /> : null}
                    </Space>
                    {completionPenaltiesLoadError ? (
                      <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                        {completionPenaltiesLoadError}
                      </Text>
                    ) : null}
                    {!completionPenaltiesLoading &&
                    !completionPenaltiesLoadError &&
                    completionManualPenalties.length === 0 ? (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        Нет штрафов с ручным вводом по договору
                      </Text>
                    ) : null}
                    {completionManualPenalties.map((row) => {
                      const st = penaltiesState[row.id];
                      if (!st) return null;
                      const typeU = String(row.type).toUpperCase();
                      return (
                        <Card key={row.id} size="small" style={{ marginTop: 12 }} title={row.name}>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            {typeU === 'FIXED'
                              ? PENALTY_TYPE_LABELS.FIXED
                              : typeU === 'PERCENT'
                                ? PENALTY_TYPE_LABELS.PERCENT
                                : row.type}
                          </Text>
                          <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <Checkbox
                              checked={st.violated}
                              onChange={(e) =>
                                updateCompletionPenaltyRow(row.id, { violated: e.target.checked })
                              }
                            >
                              Было нарушение
                            </Checkbox>
                            {st.violated ? (
                              <>
                                {row.perDay ? (
                                  <div>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                                      Дней
                                    </Text>
                                    <InputNumber
                                      min={1}
                                      precision={0}
                                      style={{ width: '100%' }}
                                      value={st.days ?? undefined}
                                      onChange={(v) =>
                                        updateCompletionPenaltyRow(row.id, { days: v ?? null })
                                      }
                                    />
                                  </div>
                                ) : null}
                                {typeU === 'FIXED' ? (
                                  <div>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                                      Сумма
                                    </Text>
                                    <InputNumber
                                      min={0}
                                      style={{ width: '100%' }}
                                      value={st.amount ?? undefined}
                                      onChange={(v) =>
                                        updateCompletionPenaltyRow(row.id, { amount: v ?? null })
                                      }
                                    />
                                  </div>
                                ) : null}
                                {row.requiresComment ? (
                                  <div>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                                      Комментарий
                                    </Text>
                                    <Input.TextArea
                                      rows={3}
                                      value={st.comment}
                                      onChange={(e) =>
                                        updateCompletionPenaltyRow(row.id, { comment: e.target.value })
                                      }
                                    />
                                  </div>
                                ) : null}
                                {typeU === 'PERCENT' ? (
                                  <Text type="secondary">
                                    Сумма будет рассчитана автоматически по ставке договора
                                  </Text>
                                ) : null}
                              </>
                            ) : null}
                          </Space>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}

              {newStatus === RouteStatus.COMPLETED &&
                selectedRoute &&
                selectedRoute.isCompanyRoute === true && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Text type="secondary" style={{ display: 'block' }}>
                      Штрафы не применяются — маршрут выполняется собственным транспортом
                    </Text>
                  </>
                )}
            </>
          )}
        </Space>
      </Modal>

      {/* Форма создания маршрута */}
      <RouteCreateForm
        visible={isCreateFormVisible}
        editingRoute={null}
        onCancel={() => {
          setIsCreateFormVisible(false);
        }}
        onSuccess={() => {
          setIsCreateFormVisible(false);
          refetch();
        }}
      />

      {/* Модальное окно редактирования маршрута */}
      <RouteCreateForm
        visible={isEditModalVisible}
        editingRoute={selectedRoute}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedRoute(null);
        }}
        onSuccess={() => {
          setIsEditModalVisible(false);
          setSelectedRoute(null);
          refetch();
        }}
      />

      {/* Модальное окно местоположения */}
      <LocationModal
        visible={isLocationModalVisible}
        routeId={selectedRouteForLocation?.id}
        cargoId={selectedRouteForLocation?.cargo?.[0]?.id}
        onCancel={() => {
          setIsLocationModalVisible(false);
          setSelectedRouteForLocation(null);
        }}
        onSave={handleSaveLocation}
        title={`Местоположение груза - Маршрут #${selectedRouteForLocation?.id}`}
      />

      {/* Модальное окно истории местоположений */}
      <LocationHistoryModal
        visible={isLocationHistoryModalVisible}
        routeId={selectedRouteForHistory?.id}
        onClose={() => {
          setIsLocationHistoryModalVisible(false);
          setSelectedRouteForHistory(null);
        }}
        title={`История местоположений - Маршрут #${selectedRouteForHistory?.id}`}
      />
    </PageLayout>
  );
};

export default RoutesPage; 